import { cn } from '@ordo/ui/cn'
import { type LucideIcon, Printer } from 'lucide-react'

interface MobileActionPillProps {
  hidden?: boolean
  onPrintAll: () => void
  primaryLabel?: string
  /** When set, the print action is unavailable and this says why. The pill
   *  shows the reason in place of the label rather than staying enabled and
   *  doing nothing on tap — a silent no-op reads as a broken export. */
  printBlockedReason?: string | null
  SecondaryIcon?: LucideIcon
  secondaryLabel?: string
  onSecondary?: () => void
}

export function MobileActionPill({
  hidden,
  onPrintAll,
  primaryLabel = 'Print Orders',
  printBlockedReason = null,
  SecondaryIcon,
  secondaryLabel,
  onSecondary,
}: MobileActionPillProps) {
  const hasSecondary = Boolean(SecondaryIcon && onSecondary && secondaryLabel)
  return (
    <div
      className={cn(
        'fixed bottom-6 left-1/2 z-overlay -translate-x-1/2 lg:hidden',
        hidden && 'pointer-events-none opacity-0',
        'transition-opacity duration-150',
      )}>
      <div className="flex items-center gap-1.5 rounded-2xl bg-card/90 p-1.5 shadow-xl ring-1 ring-black/5 backdrop-blur-lg">
        {hasSecondary && SecondaryIcon ? (
          <button
            type="button"
            onClick={onSecondary}
            className="flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-4 text-sm font-medium text-foreground/90 transition-transform hover:bg-muted/60 active:scale-95">
            <SecondaryIcon className="h-5 w-5" strokeWidth={1.75} />
            <span>{secondaryLabel}</span>
          </button>
        ) : null}
        <button
          type="button"
          onClick={onPrintAll}
          disabled={printBlockedReason !== null}
          aria-disabled={printBlockedReason !== null}
          className={cn(
            'flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-4 text-sm font-medium transition-transform',
            printBlockedReason !== null
              ? 'bg-muted text-muted-foreground'
              : 'bg-primary text-primary-foreground active:scale-95',
          )}>
          <Printer className="h-5 w-5" strokeWidth={1.75} />
          <span>{printBlockedReason ?? primaryLabel}</span>
        </button>
      </div>
    </div>
  )
}
